import { useContext, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  FaTrash,
  FaPlay,
  FaStop,
  FaMicrophone,
  FaPlus,
} from "react-icons/fa";
import { DasboardContext } from "../../context/dashboard-context";

const API = "https://app.vivasmartcare.com/api";
const MAX_RECORD_SECONDS = 30;

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onloadend = () => {
      // dataURL -> sadece base64 kısmı
      const s = r.result || "";
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.readAsDataURL(blob);
  });

const V46AlarmSettings = ({ deviceId, show, onHide }) => {
  const { userInfo } = useContext(DasboardContext);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [description, setDescription] = useState("");
  const [scheduleType, setScheduleType] = useState("times");
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [times, setTimes] = useState(["08:00"]);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioMime, setAudioMime] = useState("audio/webm");
  const [audioUrl, setAudioUrl] = useState("");
  const [recordSec, setRecordSec] = useState(0);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordTimerRef = useRef(null);

  const fetchAlarms = async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v46-alarms?deviceId=${deviceId}`);
      const data = await r.json();
      setAlarms(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Alarm listesi alınamadı:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) fetchAlarms();
    // Modal kapanırken kayıt/preview temizle
    if (!show) {
      stopRecording();
      clearRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId]);

  const resetForm = () => {
    setDescription("");
    setScheduleType("times");
    setIntervalMinutes(60);
    setTimes(["08:00"]);
    clearRecording();
    setError("");
  };

  const clearRecording = () => {
    setAudioBlob(null);
    setRecordSec(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
  };

  const startRecording = async () => {
    setError("");
    clearRecording();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Tarayıcı destekli ilk MIME'ı seç
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      let mime = "";
      for (const c of candidates) {
        if (
          typeof MediaRecorder.isTypeSupported === "function" &&
          MediaRecorder.isTypeSupported(c)
        ) {
          mime = c;
          break;
        }
      }
      const mr = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      const chunks = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || mime || "audio/webm" });
        setAudioBlob(blob);
        setAudioMime(blob.type || "audio/webm");
        setAudioUrl(URL.createObjectURL(blob));
      };
      mr.start();
      setAudioMime(mime || "audio/webm");
      setRecording(true);
      setRecordSec(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSec((s) => {
          const next = s + 1;
          if (next >= MAX_RECORD_SECONDS) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      console.error("Mikrofon hatası:", e);
      setError(
        "Mikrofona erişilemedi. Tarayıcı izinlerini kontrol edin."
      );
      setRecording(false);
    }
  };

  // Saatler editörü
  const addTime = () => setTimes((arr) => [...arr, "08:00"]);
  const updateTime = (i, v) =>
    setTimes((arr) => arr.map((t, idx) => (idx === i ? v : t)));
  const removeTime = (i) =>
    setTimes((arr) => arr.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setError("");
    if (!description.trim()) {
      setError("Lütfen alarm için bir açıklama girin.");
      return;
    }
    if (scheduleType === "times") {
      const valid = times.every((t) => /^\d{2}:\d{2}$/.test(t));
      if (times.length === 0 || !valid) {
        setError("Geçerli HH:MM saatleri girin.");
        return;
      }
    }
    if (scheduleType === "interval" && (!intervalMinutes || intervalMinutes < 1)) {
      setError("Periyot en az 1 dakika olmalı.");
      return;
    }

    setSaving(true);
    try {
      let audioData = "";
      let audioMimeFinal = audioMime;
      if (audioBlob) {
        audioData = await blobToBase64(audioBlob);
        audioMimeFinal = audioBlob.type || audioMime;
      }
      const body = {
        deviceId,
        description: description.trim(),
        audioData,
        audioMime: audioMimeFinal,
        scheduleType,
        intervalMinutes: Number(intervalMinutes) || 60,
        times,
        organization: userInfo?.company || "",
        createdBy: userInfo?.username || "",
      };
      const r = await fetch(`${API}/v46-alarms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || "Kayıt başarısız");
      }
      resetForm();
      await fetchAlarms();
    } catch (e) {
      setError(e.message || "Hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu alarmı silmek istiyor musunuz?")) return;
    try {
      await fetch(`${API}/v46-alarms/${id}`, { method: "DELETE" });
      await fetchAlarms();
    } catch (e) {
      console.error("Silme hatası:", e);
    }
  };

  const handleToggle = async (a) => {
    try {
      await fetch(`${API}/v46-alarms/${a._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !a.enabled }),
      });
      await fetchAlarms();
    } catch (e) {
      console.error("Güncelleme hatası:", e);
    }
  };

  const previewAlarmAudio = (a) => {
    if (!a.audioData) return;
    const audio = new Audio(`data:${a.audioMime};base64,${a.audioData}`);
    audio.play().catch(() => {});
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Alarm Ayarları</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h6 style={{ marginBottom: "12px" }}>Yeni Alarm</h6>

        <Form.Group className="mb-3">
          <Form.Label>Açıklama</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Örn: İlaç hatırlatması"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Zamanlama</Form.Label>
          <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
            <Form.Check
              type="radio"
              id="sched-times"
              label="Belirli saatlerde"
              checked={scheduleType === "times"}
              onChange={() => setScheduleType("times")}
            />
            <Form.Check
              type="radio"
              id="sched-interval"
              label="Periyodik"
              checked={scheduleType === "interval"}
              onChange={() => setScheduleType("interval")}
            />
          </div>

          {scheduleType === "times" && (
            <div>
              {times.map((t, i) => (
                <div
                  key={i}
                  style={{ display: "flex", gap: "8px", marginBottom: "6px" }}
                >
                  <Form.Control
                    type="time"
                    value={t}
                    onChange={(e) => updateTime(i, e.target.value)}
                    style={{ maxWidth: "140px" }}
                  />
                  {times.length > 1 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeTime(i)}
                    >
                      <FaTrash />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={addTime}>
                <FaPlus /> Saat Ekle
              </Button>
            </div>
          )}

          {scheduleType === "interval" && (
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span>Her</span>
              <Form.Control
                type="number"
                min={1}
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(e.target.value)}
                style={{ maxWidth: "100px" }}
              />
              <span>dakikada bir</span>
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Ses Kaydı</Form.Label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            {!recording && (
              <Button
                variant="danger"
                size="sm"
                onClick={startRecording}
                disabled={saving}
              >
                <FaMicrophone /> Kayıt Başlat
              </Button>
            )}
            {recording && (
              <Button variant="secondary" size="sm" onClick={stopRecording}>
                <FaStop /> Durdur ({recordSec}s)
              </Button>
            )}
            {audioUrl && !recording && (
              <>
                <audio src={audioUrl} controls style={{ height: "32px" }} />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearRecording}
                >
                  Temizle
                </Button>
              </>
            )}
            <small style={{ color: "#888" }}>
              Maks {MAX_RECORD_SECONDS}sn. Ses kaydı opsiyoneldir; yoksa
              standart uyarı sesi çalar.
            </small>
          </div>
        </Form.Group>

        {error && (
          <div
            style={{
              background: "#fdecea",
              border: "1px solid #f5c6cb",
              color: "#a94442",
              borderRadius: "6px",
              padding: "8px 12px",
              marginBottom: "12px",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Alarmı Kaydet"}
          </Button>
          <Button variant="outline-secondary" onClick={resetForm} disabled={saving}>
            Formu Temizle
          </Button>
        </div>

        <hr />
        <h6 style={{ marginBottom: "12px" }}>
          Mevcut Alarmlar {loading && "(yükleniyor...)"}
        </h6>
        {!loading && alarms.length === 0 && (
          <p style={{ color: "#888" }}>Henüz alarm eklenmemiş.</p>
        )}
        {alarms.map((a) => (
          <div
            key={a._id}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "8px",
              display: "flex",
              gap: "12px",
              alignItems: "center",
              opacity: a.enabled ? 1 : 0.55,
            }}
          >
            <Form.Check
              type="switch"
              checked={a.enabled}
              onChange={() => handleToggle(a)}
              title={a.enabled ? "Aktif" : "Pasif"}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {a.description || "(açıklama yok)"}
              </div>
              <small style={{ color: "#666" }}>
                {a.scheduleType === "interval"
                  ? `Her ${a.intervalMinutes} dakikada bir`
                  : `Saatler: ${(a.times || []).join(", ")}`}
                {a.audioData ? " • 🔊 ses kaydı var" : ""}
              </small>
            </div>
            {a.audioData && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => previewAlarmAudio(a)}
                title="Sesi dinle"
              >
                <FaPlay />
              </Button>
            )}
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDelete(a._id)}
              title="Sil"
            >
              <FaTrash />
            </Button>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Kapat
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default V46AlarmSettings;
